import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Mail,
  ArrowLeft,
  Send,
  Gamepad2,
  ShieldUser,
  MessageCircle,
  Lightbulb,
  Paperclip,
  CheckCircle2,
  X,
} from 'lucide-react';
import { api, json } from '../api';
import { usePlatform } from '../store';
import { EmailVerification } from '../components/EmailVerification';
import {
  SupportConversation,
  supportLabels,
  type SupportTicket,
} from '../components/SupportConversation';
import './contact.css';
import { supportCategories } from '../../shared/support';
const categoryIcons = {
  game: Gamepad2,
  account: ShieldUser,
  suggestion: Lightbulb,
  other: MessageCircle,
};
export function ContactPage() {
  const { data } = usePlatform();
  const [params, setParams] = useSearchParams();
  const id = params.get('id'),
    mine = params.get('tab') === 'mine' || !!id;
  const [category, setCategory] = useState('game'),
    [launchId, setLaunchId] = useState(''),
    [recentGames, setRecentGames] = useState<
      { id: string; game_name: string; version: string; created: number }[]
    >([]),
    [attachment, setAttachment] = useState<File | null>(null),
    [unread, setUnread] = useState(0),
    [body, setBody] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [email, setEmail] = useState<{ email: string; verified: boolean } | null>(null),
    [items, setItems] = useState<SupportTicket[]>([]),
    [page, setPage] = useState(1),
    [total, setTotal] = useState(0);
  useEffect(() => {
    let active = true;
    if (!data?.user) return;
    void api<{ items: any[] }>('/support/recent-games')
      .then((v) => {
        if (active) setRecentGames(v.items);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [data?.user?.id]);
  useEffect(() => {
    let active = true;
    if (!data?.user) {
      setUnread(0);
      return;
    }
    const refresh = () => {
      void api<{ unread: number }>('/support/summary')
        .then((v) => {
          if (active) setUnread(v.unread);
        })
        .catch(() => {});
    };
    refresh();
    const timer = setInterval(() => {
      if (!document.hidden) refresh();
    }, 30000);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [data?.user?.id, id, mine]);
  useEffect(() => {
    let active = true;
    setEmail(null);
    if (!data?.user) return;
    const load = () => {
      void api<{ email: string; verified: boolean }>('/email-verification')
        .then((v) => {
          if (active) setEmail(v);
        })
        .catch((e) => {
          if (active) setError(e.message);
        });
    };
    load();
    window.addEventListener('focus', load);
    return () => {
      active = false;
      window.removeEventListener('focus', load);
    };
  }, [data?.user?.id]);
  useEffect(() => {
    let active = true;
    setItems([]);
    if (mine && !id && data?.user)
      void api<{ items: SupportTicket[]; total: number }>('/support?page=' + page)
        .then((v) => {
          if (active) {
            setItems(v.items);
            setTotal(v.total);
          }
        })
        .catch((e) => {
          if (active) setError(e.message);
        });
    return () => {
      active = false;
    };
  }, [mine, id, page, data?.user?.id]);
  return (
    <div className={'contact-page' + (id ? ' contact-thread-page' : '')}>
      <Link className="contact-back" to="/">
        <ArrowLeft size={16} />
        返回首页
      </Link>
      <header className="contact-heading">
        <div>
          <h1>联系我们</h1>
          <p>你的每一个声音，我们都认真听见。</p>
        </div>
      </header>
      <section className="contact-card">
        <nav className="contact-tabs">
          <button className={!mine ? 'active' : ''} onClick={() => setParams({})}>
            提交反馈
          </button>
          <button className={mine ? 'active' : ''} onClick={() => setParams({ tab: 'mine' })}>
            我的反馈 {unread > 0 && <span className="contact-unread">{unread}</span>}
          </button>
        </nav>
        {!data?.user ? (
          <div className="contact-success">
            <Mail size={36} />
            <h2>登录后，和我们聊聊</h2>
            <p>使用注册邮箱，无需重复填写联系方式。回复和进展会保留在「我的反馈」。</p>
            <Link className="button" to="/login">
              前往登录
            </Link>
          </div>
        ) : id ? (
          <>
            <button className="contact-back" onClick={() => setParams({ tab: 'mine' })}>
              ← 返回我的反馈
            </button>
            <SupportConversation id={id} />
          </>
        ) : mine ? (
          <div className="contact-history">
            {items.map((t) => (
              <button key={t.id} onClick={() => setParams({ tab: 'mine', id: t.id })}>
                <div>
                  <b>{t.game_name || supportLabels[t.category]}</b>
                  <span className={'support-state ' + t.status}>{supportLabels[t.status]}</span>
                </div>
                <p>{t.body}</p>
                <small>{new Date(t.created_at).toLocaleString()} · 查看沟通记录 →</small>
              </button>
            ))}
            {!items.length && <p>暂时没有反馈记录，想说的话随时告诉我们。</p>}
            {total > 20 && (
              <div>
                <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                  上一页
                </button>{' '}
                {page}{' '}
                <button disabled={page * 20 >= total} onClick={() => setPage(page + 1)}>
                  下一页
                </button>
              </div>
            )}
          </div>
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (busy) return;
              setBusy(true);
              setError('');
              try {
                const r = await api<{ id: string }>(
                  '/support',
                  json('POST', {
                    category,
                    body,
                    ...(category === 'game' && launchId ? { launchId } : {}),
                  }),
                );
                if (attachment) {
                  const upload = new FormData();
                  upload.append('image', attachment);
                  try {
                    await api('/support/' + r.id + '/images', { method: 'POST', body: upload });
                  } catch {
                    setError('反馈已提交，但截图上传失败，请在下方详情中重新添加截图。');
                  }
                }
                setAttachment(null);
                setBody('');
                setParams({ tab: 'mine', id: r.id });
              } catch (e) {
                setError((e as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <fieldset>
              <legend>这次想和我们聊什么？</legend>
              <div className="contact-types">
                {supportCategories.map((c) => {
                  const Icon = categoryIcons[c.value];
                  return (
                    <button
                      type="button"
                      key={c.value}
                      aria-pressed={category === c.value}
                      onClick={() => setCategory(c.value)}
                      disabled={busy}
                    >
                      <Icon size={32} strokeWidth={1.8} />
                      <strong>{c.label}</strong>
                      <small>{c.description}</small>
                    </button>
                  );
                })}
              </div>
            </fieldset>
            {category === 'game' && (
              <label className="contact-game-choice">
                关联哪一次游玩？
                <select value={launchId} onChange={(e) => setLaunchId(e.target.value)}>
                  <option value="">不关联 / 没有找到对应记录</option>
                  {recentGames.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.game_name || '游戏'} · {g.version} · {new Date(g.created).toLocaleString()}
                    </option>
                  ))}
                </select>
                <small>
                  可关联近14天的运行日志；当时的画面无法事后补拍。在游戏内提交可采集现场。
                </small>
              </label>
            )}
            <label>
              详细描述
              <textarea
                required
                minLength={2}
                maxLength={2000}
                rows={4}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="发生了什么？告诉我们具体情况，方便更快帮你解决。"
              />
            </label>
            <small className="contact-counter">{body.length} / 2000</small>
            <div className="contact-attachment-row">
              <label className="contact-attach">
                <Paperclip size={18} />
                添加截图
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={busy}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      setError('截图不能超过5 MB');
                      return;
                    }
                    setError('');
                    setAttachment(file);
                  }}
                />
              </label>
              <small>选填，帮助我们了解问题。</small>
              {attachment && (
                <span className="contact-selected-file">
                  {attachment.name}
                  <button
                    type="button"
                    aria-label="移除截图"
                    disabled={busy}
                    onClick={() => setAttachment(null)}
                  >
                    <X size={14} />
                  </button>
                </span>
              )}
            </div>
            <div className="contact-email">
              <Mail size={24} />
              <div>
                <small>使用注册邮箱联系你</small>
                <strong>
                  {email?.email
                    ? email.email.replace(/^(.).*(@.*)$/, '$1***$2')
                    : '正在读取账号邮箱…'}
                </strong>
              </div>
              <span className={email?.verified ? 'verified' : ''}>
                {email?.verified && <CheckCircle2 size={15} />}{' '}
                {email?.verified ? '已验证' : '待验证'}
              </span>
            </div>
            {email && !email.verified && (
              <>
                <p className="contact-hint">
                  邮箱尚未验证，请先完成验证再提交。填写的内容会保留在当前页面。
                </p>
                <EmailVerification compact />
              </>
            )}
            <button className="button contact-send" disabled={busy || !email?.verified}>
              <Send size={17} />
              {busy ? '正在送达…' : '发送反馈'}
            </button>
            <p className="contact-hint">回复会通过站内消息提醒，也可在「我的反馈」查看。</p>
          </form>
        )}
        {error && <p role="alert">{error}</p>}
      </section>
    </div>
  );
}
